import React, { useEffect, useState, createContext } from 'react';
import { useForm, Controller, useWatch, set } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { patientRegistrationSchema } from '../../validation/patientRegistrationSchema';
import {
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Typography,
  Divider,
  Grid,
  Card,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Backdrop,
  CircularProgress,
  Box,
} from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SaveIcon from '@mui/icons-material/Save';
import MDBox from 'components/MDBox';
import MDTypography from 'components/MDTypography';
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import Footer from 'examples/Footer';
import { api } from '../../api';
import Swal from 'sweetalert2';
import DocumentUpload from './DocumentUpload';
import Timeline from './Timeline';
import MDDatePicker from 'components/MDDatePicker';
import dayjs from 'dayjs';
import PatientHistory from './PatientHistory';
import { getCurrentUserId } from '../opdAppointments/constants';

const selectBoxHeight = 45; // Set the height for the select box

// Religion options constant
const RELIGION_OPTIONS = [
  'Hinduism',
  'Islam',
  'Christianity',
  'Sikhism',
  'Buddhism',
  'Jainism',
  'Others'
];

const PatientRegistrationContext = createContext();

const defaultValues = {
  first_name: '',
  last_name: '',
  gender: 'F',
  marital_status: 'married', // Default to married for testing
  date_of_birth: '',
  married_for_years: '',
  trying_for: '',
  religion: '',
  nationality: '',
  email_address: '',
  primay_mobile_number: '',
  whatsapp_number: '',
  address_line_1: '',
  country_id: 100,
  phonecode: '91', // Default phone code for India
  state: '',
  state_id: null, // State ID for India states
  city: '',
  postal_code: '',
  permanentSameAsResidential: true,
  permanent_address: {
    address_line_1: '',
    address_line_2: '',
    country_id: 100,
    state: '',
    state_id: null, // State ID for India states
    city: '',
    postal_code: '',
  },
  doctor_id: 0,
  spouse_first_name: '',
  spouse_last_name: '',
  spouse_gender: '',
  spouse_date_of_birth: '',
  spouse_mobile_number: '',
  occupation: '',
  spouse_occupation: '',
  address_line_2: '',
  referred_by_id: 0,
  referred_by_name: '',
  referred_by_doctor_id: null, // For when referred by doctor
  referred_by_doctor_name: '', // For when referred by doctor
  socio_economic_status_id: '', // Ensure default is '' to avoid undefined for select
  custom_doctor_name: '', // For custom doctor name when "Other" is selected
};

// const countries = [
//   { id: 100, country_name: "India", nationality: "Indian" },
//   { id: 101, country_name: "USA", nationality: "American" },
//   // ...other countries
// ];

function PatientRegistration() {
  const {
    control,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues,
    resolver: joiResolver(patientRegistrationSchema),
    mode: 'onBlur',
  });

  const [countries, setCountries] = useState([]);
  // Add state for nationality if not already present
  const [nationality, setNationality] = useState('');
  const [phonecode, setPhonecode] = useState('');
  const [doctors, setDoctors] = useState([]);
  const [referredBy, setReferredBy] = useState([]);
  const [referredByDoctor, setReferredByDoctor] = useState([]);
  const [socioEconomicStatus, setSocioEconomicStatus] = useState([]);
  // State for handling "Other" option in referred by doctor
  const [showCustomDoctorInput, setShowCustomDoctorInput] = useState(false);
  // State for managing states list for India
  const [states, setStates] = useState([]);
  // Loading state for disabling the window
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // State to manage the current step in the registration process
  // Set when this screen is opened from a guest appointment's "Register as
  // Patient" deep link (?guestAppointmentId=...&mobile=...&name=...) —
  // used to auto-tag the new contact back onto that appointment on submit.
  const [guestAppointmentId, setGuestAppointmentId] = useState(null);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const guestId = params.get('guestAppointmentId');
    if (!guestId) return;

    setGuestAppointmentId(guestId);

    const mobile = params.get('mobile') || '';
    const name = (params.get('name') || '').trim();
    const [firstName, ...rest] = name.split(' ').filter(Boolean);

    if (mobile) setValue('primay_mobile_number', mobile);
    if (firstName) setValue('first_name', firstName);
    if (rest.length) setValue('last_name', rest.join(' '));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    api.master.getActiveCountries().then((res) => {
      console.table(res);
      if (res?.data?.countries?.length) {
        setCountries(res.data.countries);
        // Set default nationality to first country if not already set
        if (!nationality && res.data.countries[0]?.country_name) {
          setNationality(res.data.countries[0].country_name);
          console.log('Default nationality set to:', res.data.countries[0].country_name);
        }
      }
    });
  }, []);

  useEffect(() => {
    api.contacts.getDoctorsList().then((res) => {
      if (res?.data) setDoctors(res.data);
    });
  }, []);

  useEffect(() => {
    api.contacts.getPatientParams().then((res) => {
      if (res?.data?.params?.param_group_members) {
        const members = res.data.params.param_group_members;

        const referredByMember = members.find((m) => m.name === 'Referred By');
        if (referredByMember) {
          setReferredBy(referredByMember.param_group_member_options || []);
        }

        const referredByDoctorMember = members.find((m) => m.name === 'Referred By Doctor');
        if (referredByDoctorMember) {
          const doctorOptions = [...(referredByDoctorMember.param_group_member_options || [])];
          // Add "Other" option to the list
          doctorOptions.push({ id: 'other', option_value: 'Other' });
          setReferredByDoctor(doctorOptions);
        }
        const socioEconomicStatusMember = members.find((m) => m.name === 'Socio Economic Status');
        if (socioEconomicStatusMember) {
          setSocioEconomicStatus(socioEconomicStatusMember.param_group_member_options || []);
        }
      }
    });
  }, []);

  // Load states for India (country_id = 100)
  useEffect(() => {
    api.master.getActiveStates(100).then((res) => {
      console.log('States response:', res);
      if (res?.data?.states?.length) {
        setStates(res.data.states);
      }
    }).catch((error) => {
      console.error('Error loading states:', error);
    });
  }, []);

  console.log('referredBy:', referredBy);
  console.log('states:', states);

  // Modify country selection handler
  const handleCountryChange = (countryId) => {
    const selectedCountry = countries.find((country) => country.id === countryId);
    if (selectedCountry) {
      setValue('phonecode', String(selectedCountry.phonecode));
      setPhonecode(String(selectedCountry.phonecode));
      // Clear state field when country changes
      setValue('state', '');
      setValue('state_id', null);
    }
  };
  const handleNationalityChange = (countryId) => {
    const selectedCountry = countries.find((country) => country.nationality === countryId);
    if (selectedCountry) {
      setValue('phonecode', String(selectedCountry.phonecode));
      setPhonecode(String(selectedCountry.phonecode));
      console.log('selectedCountry', selectedCountry);
    } else {
      console.log('countries', countries);
    }
  };

  // Add state for controlling Permanent Address accordion
  const [permanentAccordionOpen, setPermanentAccordionOpen] = useState(false);

  // Watchers for conditional logic
  const marital_status = useWatch({ control, name: 'marital_status' });
  const country_id = useWatch({ control, name: 'country_id' });
  const permanentSameAsResidential = useWatch({ control, name: 'permanentSameAsResidential' });
  const referred_by_name = useWatch({ control, name: 'referred_by_name' });
  const nationality_value = useWatch({ control, name: 'nationality' });
  
  // Effect to auto-open/close the accordion based on checkbox
  useEffect(() => {
    setPermanentAccordionOpen(!permanentSameAsResidential);
  }, [permanentSameAsResidential]);
  
  const residentialAddress = {
    address_line_1: watch('address_line_1'),
    address_line_2: watch('address_line_2'),
    country_id: watch('country_id'),
    state: watch('state'),
    state_id: watch('state_id'),
    city: watch('city'),
    postal_code: watch('postal_code'),
  };

  // Copy or clear permanent address based on checkbox
  useEffect(() => {
    if (permanentSameAsResidential) {
      setValue('permanent_address', { ...residentialAddress });
    } else {
      setValue('permanent_address', {
        address_line_1: '',
        address_line_2: '',
        country_id: 100,
        state: '',
        city: '',
        postal_code: '',
      });
    }
    // eslint-disable-next-line
  }, [
    permanentSameAsResidential,
    residentialAddress.address_line_1,
    residentialAddress.address_line_2,
    residentialAddress.country_id,
    residentialAddress.state,
    residentialAddress.state_id,
    residentialAddress.city,
    residentialAddress.postal_code,
  ]);

  // Handle "Other" option selection for referred by doctor
  useEffect(() => {
    if (referred_by_name === 'Other') {
      setShowCustomDoctorInput(true);
      setValue('custom_doctor_name', ''); // Reset custom doctor name
      setValue('referred_by_doctor_name', ''); // Reset referred by doctor name
      setValue('referred_by_doctor_id', null); // Set doctor ID to null for "Other"
    } else {
      setShowCustomDoctorInput(false);
      setValue('custom_doctor_name', ''); // Clear custom doctor name when not needed
    }
  }, [referred_by_name, setValue]);

  // Handle referred_by_id changes to clear appropriate fields
  useEffect(() => {
    const referredById = watch('referred_by_id');
    if (referredById === 3) {
      // If referred by doctor, clear non-doctor fields and ensure referred_by_name has a value
      if (!watch('referred_by_name')) {
        setValue('referred_by_name', ''); // Ensure it's at least an empty string, not undefined
      }
    } else {
      // If not referred by doctor, clear doctor fields
      setValue('referred_by_doctor_name', '');
      setValue('referred_by_doctor_id', null);
      setValue('custom_doctor_name', '');
      setShowCustomDoctorInput(false);
    }
  }, [watch('referred_by_id'), setValue]);

  // Clear state values when country changes
  useEffect(() => {
    setValue('state', ''); // Clear residential state when country changes
    setValue('state_id', null); // Clear residential state_id when country changes
  }, [watch('country_id'), setValue]);

  useEffect(() => {
    setValue('permanent_address.state', ''); // Clear permanent state when permanent country changes
    setValue('permanent_address.state_id', null); // Clear permanent state_id when permanent country changes
  }, [watch('permanent_address.country_id'), setValue]);

  // Update checkbox handler
  const handlePermanentCheckbox = (e) => {
    const checked = e.target.checked;
    // No need to set permanentSameAsResidential as it's managed by React Hook Form
    setPermanentAccordionOpen(!checked); // Open accordion if unchecked
    if (checked) {
      setValue('permanent_address', { ...residentialAddress });
    } else {
      setValue('permanent_address', {
        address_line_1: '',
        address_line_2: '',
        country_id: 100,
        state: '',
        state_id: null,
        city: '',
        postal_code: '',
      });
    }
  };

  const onSubmit = (data) => {
    console.log('Submitted data:', data);

    setLoading(true);

    const selectedSocioEconomicStatus = socioEconomicStatus.find(
      (option) => option.id === data.socio_economic_status_id,
    );
    const socioEconomicStatusValue = selectedSocioEconomicStatus
      ? selectedSocioEconomicStatus.option_value
      : '';

    // Transform the form data into the required API structure
    const payload = {
      patient: {
        first_name: data.first_name,
        last_name: data.last_name,
        gender: data.gender || 'F', // Default to 'F' if not provided
        marital_status: data.marital_status,
        religion: data.religion,
        nationality: data.nationality,
        email_address: data.email_address || null,
        occupation: data.occupation, // Default value or add to form
        date_of_birth: data.date_of_birth,
        married_for: data.married_for_years,
        trying_for: data.trying_for,
        socio_economic_status_id: data.socio_economic_status_id, // Default value or add to form
        socio_economic_status: socioEconomicStatusValue, // Default value or add to form
        country_id: data.country_id,
        phones: [
          {
            country_code: data.phonecode,
            phone_type: 'mobile',
            phone_number: data.primay_mobile_number,
            is_default: 'Y',
          },
          ...(data.whatsapp_number
            ? [
                {
                  country_code: data.phonecode,
                  phone_type: 'whatsapp',
                  phone_number: data.whatsapp_number,
                  is_default: 'N',
                },
              ]
            : []),
        ],
        addresses: [
          {
            address_line_1: data.address_line_1,
            address_line_2: data.address_line_2,
            city: data.city,
            state: data.state,
            state_id: data.state_id,
            country_id: data.country_id,
            postal_code: data.postal_code,
            address_type: 'residential',
            is_default: 'Y',
          },
          ...(data.permanentSameAsResidential
            ? [
                {
                  address_line_1: data.permanent_address.address_line_1,
                  address_line_2: data.permanent_address.address_line_2,
                  city: data.permanent_address.city,
                  state: data.permanent_address.state,
                  state_id: data.permanent_address.state_id,
                  country_id: data.permanent_address.country_id,
                  postal_code: data.permanent_address.postal_code,
                  address_type: 'permanent',
                  is_default: 'N',
                },
              ]
            : []),
        ],
      },
      ...(data.marital_status === 'married' || data.marital_status === 'widowed'
        ? {
            spouse: {
              first_name: data.spouse_first_name,
              last_name: data.spouse_last_name,
              gender: data.spouse_gender,
              marital_status: data.marital_status, // Same as patient
              religion: data.religion, // Same as patient
              nationality: data.nationality, // Same as patient
              occupation: data.spouse_occupation, // Default value or add to form
              date_of_birth: data.spouse_date_of_birth,
              married_for: data.married_for_years, // Same as patient
              trying_for: data.trying_for, // Same as patient
              socio_economic_status_id: data.socio_economic_status_id, // Same as patient
              socio_economic_status: socioEconomicStatusValue, // Same as patient
              country_id: data.country_id, // Same as patient
              phones: [
                {
                  country_code: data.phonecode,
                  phone_type: 'mobile',
                  phone_number: data.spouse_mobile_number,
                  is_default: 'Y',
                },
              ],
              addresses: [
                {
                  address_line_1: data.address_line_1, // Same residential address
                  address_line_2: data.address_line_2,
                  city: data.city,
                  state: data.state,
                  state_id: data.state_id,
                  country_id: data.country_id,
                  postal_code: data.postal_code,
                  address_type: 'residential',
                  is_default: 'Y',
                },
                ...(data.permanentSameAsResidential
                  ? [
                      {
                        address_line_1: data.permanent_address.address_line_1,
                        address_line_2: data.permanent_address.address_line_2,
                        city: data.permanent_address.city,
                        state: data.permanent_address.state,
                        state_id: data.permanent_address.state_id,
                        country_id: data.permanent_address.country_id,
                        postal_code: data.permanent_address.postal_code,
                        address_type: 'permanent',
                        is_default: 'N',
                      },
                    ]
                  : []),
              ],
            },
          }
        : {}),
      doctor_id: data.doctor_id, // Static value
      referred_by_id: data.referred_by_id, // Static value
      // Send different fields based on referred by selection
      ...(data.referred_by_id === 3 ? {
        // If referred by doctor
        referred_by_doctor_name: data.referred_by_name === 'Other' ? data.custom_doctor_name : data.referred_by_doctor_name,
        referred_by_doctor_id: data.referred_by_name === 'Other' ? null : data.referred_by_doctor_id,
      } : {
        // If referred by other sources (not doctor)
        referred_by_name: data.referred_by_name === 'Other' ? data.custom_doctor_name : data.referred_by_name,
      }),
    };

    // Now send this payload to your API
    api.contacts
      .patientRegistration(payload)
      .then((response) => {
        console.log('Registration successful:', response);
        // Extract required values
        const patient_id = response.data.response.patient.id;
        const spouse_id = response.data.response.spouseId;
        const external_code = response.data.response.patient.external_code;

        // Store in localStorage
        localStorage.setItem('patient_id', patient_id);
        localStorage.setItem('spouse_id', spouse_id);
        localStorage.setItem('nationality', data.nationality);
        localStorage.setItem('external_code', external_code);

        Swal.fire({
          icon: 'success',
          title: 'Patient registered successfully!',
          showConfirmButton: true,
        });
        // Reset the form after successful registration
        // reset(defaultValues);

        // Opened from a guest appointment's "Register as Patient" link —
        // auto-tag the new contact back onto that appointment (converts it
        // from guest to a normal, checkinable appointment).
        if (guestAppointmentId) {
          api.appointment
            .mapPatient(guestAppointmentId, {
              irm_contacts_id: patient_id,
              updated_by: getCurrentUserId(),
            })
            .catch((error) => {
              console.error('Failed to auto-link guest appointment to new patient:', error);
            });
        }


        api.contacts.getPatientDetailsByExternalCode(external_code)
          .then((response) => {
            console.log('Fetched patient details:', response);
            // Extracting the couple_info values
            const coupleId = response.data.couple_info.id;
            const coupleExternalCode = response.data.couple_info.external_code;
            localStorage.setItem('couple_id', coupleId);
            localStorage.setItem('couple_external_code', coupleExternalCode);

          })
          .catch((error) => {
            if (!error.handled) {
              console.error('Failed to fetch patient details:', error);
            }
          });
        setStep(2);
      })
      .catch((error) => {
        // Don't show alert if error is already handled (like 403)
        if (error.handled) {
          console.log('Error already handled by interceptor');
          return;
        }
        
        console.error('Registration failed:', error);
        
        if (error.response) {
          console.error('API error response:', error.response.data);
          // alert("Patient registration failed: " + JSON.stringify(error.response.data));

          Swal.fire({
            icon: 'error',
            title: 'Patient registration failed',
            text:
              (error.response.data?.messages
                ? Object.values(error.response.data.messages).join(', ')
                : error.response.data?.message) || 'An error occurred during registration.',
            showConfirmButton: true,
          });
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Registration Failed',
            text: 'Patient registration failed. Please try again.',
            showConfirmButton: true,
          });
        }
      })
      .finally(() => {
        setLoading(false); // Hide the loader after the request is complete
      });

    console.log('Transformed payload:', payload);
    console.log('🔍 Referred By Debug:', {
      referred_by_id: data.referred_by_id,
      referred_by_name: data.referred_by_name,
      referred_by_doctor_name: data.referred_by_doctor_name,
      referred_by_doctor_id: data.referred_by_doctor_id,
      custom_doctor_name: data.custom_doctor_name
    });
    // alert("Form submitted successfully!XXXX");
  };

  const onError = (errors) => {
    console.log('Form errors:', errors);
    // alert('Form has errors. Check the console for details.');
  };

  // Loader overlay component
  const LoaderOverlay = () => (
    <Backdrop
      sx={{
        color: '#fff',
        zIndex: (theme) => theme.zIndex.drawer + 9999,
        backgroundColor: 'rgba(255,255,255,0.5)',
      }}
      open={loading}
    >
      <CircularProgress color="primary" size={80} thickness={4} />
    </Backdrop>
  );

  // Add this useEffect to update country when nationality changes
  useEffect(() => {
    // Watch for changes in nationality field
    const subscription = watch((value, { name }) => {
      if (name === 'nationality') {
        const selectedCountry = countries.find(
          (country) => country.nationality === value.nationality,
        );
        if (selectedCountry) {
          setValue('country_id', selectedCountry.id);
          setValue('phonecode', selectedCountry.phonecode);
          setPhonecode(selectedCountry.phonecode);
          console.log('config.headers', selectedCountry);
          setNationality(selectedCountry.nationality);
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [countries, setValue, watch]);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      {/* Loader overlay */}
      <LoaderOverlay />

     

      <MDBox pt={4} pb={3}>
        {/* {showDocumentUpload ? ( */}
        {step === 3 && <PatientHistory />}
        {step === 2 && <DocumentUpload label="Upload Patient Documents" />}
        {step === 1 && (
          <Grid container spacing={6}>
            <Grid item xs={12}>
              <Card>
                <MDBox
                  mx={2}
                  mt={-3}
                  py={3}
                  px={2}
                  variant="gradient"
                  bgColor="logo"
                  borderRadius="lg"
                  coloredShadow="logo"
                >
                  <MDTypography variant="h6" color="white">
                    Patient Registration
                  </MDTypography>
                </MDBox>
                <form
                  onSubmit={handleSubmit(onSubmit, onError)}
                  style={{ padding: '20px' }}
                  noValidate
                >
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={marital_status === 'married' ? 6 : 12}>
                      <MDBox>
                        <MDTypography
                          variant="h5"
                          fontWeight="bold"
                          gutterBottom
                          sx={{
                            textAlign: 'center',
                            padding: '12px',
                            borderBottom: '1px solid #00798E',
                            display: 'flex',
                            justifyContent: 'center',
                          }}
                        >
                          Patient Details
                        </MDTypography>
                      </MDBox>

                      {/* Name Row */}
                      <Grid container spacing={2}>
                        {/* First Name - Half Width */}
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="first_name"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                                label={
                                  <span>
                                    First Name <span style={{ color: 'red' }}>*</span>
                                  </span>
                                }
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                error={!!errors.first_name}
                                helperText={errors.first_name?.message}
                                sx={{
                                  '& .MuiInputBase-input': { 
                                    textTransform: 'uppercase' 
                                  },
                                }}
                              />
                            )}
                          />
                        </Grid>

                        {/* Last Name - Half Width */}
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="last_name"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                                label={
                                  <span>
                                    Last Name <span style={{ color: 'red' }}>*</span>
                                  </span>
                                }
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                error={!!errors.last_name}
                                helperText={errors.last_name?.message}
                                sx={{
                                  '& .MuiInputBase-input': { 
                                    textTransform: 'uppercase' 
                                  },
                                }}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* Gender & Marital Status Row */}
                      <Grid container spacing={2}>
                        {/* Gender - Half Width */}
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="gender"
                            control={control}
                            render={({ field }) => (
                              <FormControl fullWidth margin="normal" error={!!errors.gender}>
                                <InputLabel id="gender-label">
                                  <span>
                                    Gender <span style={{ color: 'red' }}>*</span>
                                  </span>
                                </InputLabel>
                                <Select
                                  {...field}
                                  labelId="gender-label"
                                  label={
                                    <span>
                                      Gender <span style={{ color: 'red' }}>*</span>
                                    </span>
                                  }
                                  sx={{
                                    height: selectBoxHeight,
                                  }}
                                >
                                  <MenuItem value="F">Female</MenuItem>
                                  <MenuItem value="M">Male</MenuItem>
                                </Select>
                                <Typography color="error" variant="caption">
                                  {errors.gender?.message}
                                </Typography>
                              </FormControl>
                            )}
                          />
                        </Grid>

                        {/* Marital Status - Half Width */}
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="marital_status"
                            control={control}
                            render={({ field }) => (
                              <FormControl
                                fullWidth
                                margin="normal"
                                error={!!errors.marital_status}
                              >
                                <InputLabel id="marital-status-label">Marital Status</InputLabel>
                                <Select
                                  {...field}
                                  labelId="marital-status-label"
                                  label="Marital Status"
                                  sx={{
                                    height: selectBoxHeight,
                                  }}
                                >
                                  <MenuItem value="single">Single</MenuItem>
                                  <MenuItem value="married">Married</MenuItem>
                                  <MenuItem value="divorced">Divorced</MenuItem>
                                  <MenuItem value="widowed">Widowed</MenuItem>
                                </Select>
                                <Typography color="error" variant="caption">
                                  {errors.marital_status?.message}
                                </Typography>
                              </FormControl>
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* Date of Birth Row */}
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Controller
                            name="date_of_birth"
                            control={control}
                            rules={{ required: true }}
                            render={({ field }) => (
                              <FormControl fullWidth margin="normal" error={!!errors.date_of_birth}>
                                <MDDatePicker
                                  value={field.value || ''} 
                                  onChange={(apiDate) => {
                                    field.onChange(apiDate);
                                  }}
                                  label={
                                    <span>
                                      Date of Birth <span style={{ color: 'red' }}>*</span>
                                    </span>
                                  }
                                  maxDate={dayjs()} // disableFuture={true}
                                  textFieldProps={{ sx: { width: '100%' } }}
                                />
                                <Typography color="error" variant="caption">
                                  {errors.date_of_birth?.message}
                                </Typography>
                              </FormControl>
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* Married For & Trying For Row (conditional) */}
                      {marital_status === 'married' && (
                        <Grid container spacing={2}>
                          {/* Married For Years - Half Width */}
                          <Grid item xs={12} md={6}>
                            <Controller
                              name="married_for_years"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                                  label="Married For (Years)"
                                  variant="outlined"
                                  fullWidth
                                  margin="normal"
                                  error={!!errors.married_for_years}
                                  helperText={errors.married_for_years?.message}
                                  sx={{
                                    '& .MuiInputBase-input': { 
                                      textTransform: 'uppercase' 
                                    },
                                  }}
                                />
                              )}
                            />
                          </Grid>

                          {/* Trying For - Half Width */}
                          <Grid item xs={12} md={6}>
                            <Controller
                              name="trying_for"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                                  label="Trying For"
                                  variant="outlined"
                                  fullWidth
                                  margin="normal"
                                  error={!!errors.trying_for}
                                  helperText={errors.trying_for?.message}
                                  sx={{
                                    '& .MuiInputBase-input': { 
                                      textTransform: 'uppercase' 
                                    },
                                  }}
                                />
                              )}
                            />
                          </Grid>
                        </Grid>
                      )}

                      {/* Religion & Nationality Row */}
                      <Grid container spacing={2}>
                        {/* Religion - Half Width */}
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="religion"
                            control={control}
                            render={({ field }) => (
                              <FormControl fullWidth margin="normal" error={!!errors.religion}>
                                <InputLabel id="religion-label">Religion</InputLabel>
                                <Select
                                  {...field}
                                  labelId="religion-label"
                                  label="Religion"
                                  sx={{
                                    height: selectBoxHeight,
                                  }}
                                >
                                  {RELIGION_OPTIONS.map((religion) => (
                                    <MenuItem key={religion} value={religion}>
                                      {religion}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Typography color="error" variant="caption">
                                  {errors.religion?.message}
                                </Typography>
                              </FormControl>
                            )}
                          />
                        </Grid>

                        {/* Nationality - Half Width */}
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="nationality"
                            control={control}
                            render={({ field }) => (
                              <FormControl fullWidth margin="normal" error={!!errors.nationality}>
                                <InputLabel id="nationality-label">Nationality</InputLabel>
                                <Select
                                  {...field}
                                  labelId="nationality-label"
                                  label="Nationality"
                                  sx={{
                                    height: selectBoxHeight,
                                  }}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    handleNationalityChange(e.target.value);
                                  }}
                                >
                                  {countries.map((country) => (
                                    <MenuItem key={country.id} value={country.nationality}>
                                      {country.nationality}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Typography color="error" variant="caption">
                                  {errors.nationality?.message}
                                </Typography>
                              </FormControl>
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* Primary & WhatsApp Number Row */}
                      <Grid container spacing={2}>
                        {/* Primary Mobile Number - Half Width */}
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="primay_mobile_number"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={
                                  <span>
                                    Primary Mobile Number <span style={{ color: 'red' }}>*</span>
                                  </span>
                                }
                                placeholder={nationality === "Indian" ? 
                                  "Primary Mobile Number (10 digit)" : 
                                  "Primary Mobile Number (Max 15 digit)"}
                                variant="outlined"
                                type="text"
                                fullWidth
                                margin="normal"
                                error={!!errors.primay_mobile_number}
                                helperText={
                                  errors.primay_mobile_number?.message || 
                                  (nationality_value === 'Indian' ? '10 digits required' : '8 to 15 digits required')
                                }
                                InputProps={{
                                  inputProps: {
                                    maxLength: nationality_value === 'Indian' ? 10 : 15,
                                    pattern: '[0-9]*',
                                    inputMode: 'numeric',
                                  },
                                }}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/[^0-9]/g, '');
                                  field.onChange(value);
                                }}
                              />
                            )}
                          />
                        </Grid>

                        {/* WhatsApp Number - Half Width */}
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="whatsapp_number"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Optional Number (WhatsApp)"
                                variant="outlined"
                                type="text"
                                fullWidth
                                margin="normal"
                                error={!!errors.whatsapp_number}
                                helperText={
                                  errors.whatsapp_number?.message || 
                                  (nationality_value === 'Indian' ? '10 digits required' : '8 to 15 digits required')
                                }
                                InputProps={{
                                  inputProps: {
                                    maxLength: nationality_value === 'Indian' ? 10 : 15,
                                    pattern: '[0-9]*',
                                    inputMode: 'numeric',
                                  },
                                }}
                                onChange={(e) => {
                                  const value = e.target.value.replace(/[^0-9]/g, '');
                                  field.onChange(value);
                                }}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* Email Address Row */}
                      <Grid container spacing={2}>
                        {/* Email Address - Full Width */}
                        <Grid item xs={12}>
                          <Controller
                            name="email_address"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label="Email Address"
                                variant="outlined"
                                type="email"
                                fullWidth
                                margin="normal"
                                placeholder="Enter email address"
                                error={!!errors.email_address}
                                helperText={errors.email_address?.message || 'Optional: Enter valid email address'}
                                InputProps={{
                                  style: {
                                    fontSize: '0.875rem',
                                  },
                                }}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* Occupation & Socio Economic Status Row */}
                      <Grid container spacing={2}>
                        {/* Occupation - Half Width */}
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="occupation"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={
                                  <span>
                                    Occupation <span style={{ color: 'red' }}>*</span>
                                  </span>
                                }
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                error={!!errors.occupation}
                                helperText={errors.occupation?.message || ''}
                                InputProps={{
                                  style: {
                                    fontSize: '0.875rem',
                                  },
                                }}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                              />
                            )}
                          />
                        </Grid>

                        {/* Socio Economic Status - Half Width */}
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="socio_economic_status_id"
                            control={control}
                            render={({ field }) => (
                              <FormControl
                                fullWidth
                                margin="normal"
                                error={!!errors.socio_economic_status_id}
                              >
                                <InputLabel
                                  id="socio-economic-status-label"
                                  shrink={true}
                                  sx={{
                                    fontSize: '0.875rem',
                                    background: 'white',
                                    padding: '0 4px',
                                    marginLeft: '-4px',
                                  }}
                                >
                                  <span>
                                    Socio Economic Status <span style={{ color: 'red' }}>*</span>
                                  </span>
                                </InputLabel>
                                <Select
                                  {...field}
                                  labelId="socio-economic-status-label"
                                  label={
                                    <span>
                                      Socio Economic Status <span style={{ color: 'red' }}>*</span>
                                    </span>
                                  }
                                  sx={{
                                    height: selectBoxHeight,
                                    fontSize: '0.875rem',
                                  }}
                                >
                                  {socioEconomicStatus.map((option) => (
                                    <MenuItem
                                      key={option.id}
                                      value={option.id}
                                      sx={{ fontSize: '0.875rem' }}
                                    >
                                      {option.option_value}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Typography color="error" variant="caption">
                                  {errors.socio_economic_status_id?.message}
                                </Typography>
                              </FormControl>
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* Residential Address Section */}
                      <MDBox mt={4} mb={2}>
                        <MDTypography variant="h6" fontWeight="bold" gutterBottom>
                          Residential Address
                        </MDTypography>
                        <Divider sx={{ mb: 2, background: '#00798E' }} />
                      </MDBox>
                      {/* Address Line 1 Row */}
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Controller
                            name="address_line_1"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                                label={
                                  <span>
                                    Address Line 1 <span style={{ color: 'red' }}>*</span>
                                  </span>
                                }
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                error={!!errors.address_line_1}
                                helperText={errors.address_line_1?.message}
                                sx={{
                                  '& .MuiInputBase-input': { 
                                    textTransform: 'uppercase' 
                                  },
                                }}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                      {/* Address Line 2 Row */}
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Controller
                            name="address_line_2"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                                label="Address Line 2"
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                error={!!errors.address_line_2}
                                helperText={errors.address_line_2?.message}
                                sx={{
                                  '& .MuiInputBase-input': { 
                                    textTransform: 'uppercase' 
                                  },
                                }}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* Add the hidden phonecode field */}
                      <Controller
                        name="phonecode"
                        control={control}
                        render={({ field }) => <input type="hidden" {...field} />}
                      />

                      {/* Country & State Row */}
                      <Grid container spacing={2}>
                        {/* Country - Half Width */}
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="country_id"
                            control={control}
                            render={({ field }) => (
                              <FormControl fullWidth margin="normal" error={!!errors.country_id}>
                                <InputLabel id="country-select-label" shrink>
                                  <span>
                                    Country <span style={{ color: 'red' }}>*</span>
                                  </span>
                                </InputLabel>
                                <Select
                                  {...field}
                                  labelId="country-select-label"
                                  label={
                                    <span>
                                      Country <span style={{ color: 'red' }}>*</span>
                                    </span>
                                  }
                                  sx={{
                                    height: selectBoxHeight,
                                    fontSize: '0.875rem',
                                  }}
                                  onChange={(e) => {
                                    field.onChange(e);
                                    handleCountryChange(e.target.value);
                                  }}
                                >
                                  {countries.map((country) => (
                                    <MenuItem
                                      key={country.id}
                                      value={country.id}
                                      sx={{ fontSize: '0.875rem' }}
                                    >
                                      {country.country_name}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Typography color="error" variant="caption">
                                  {errors.country_id?.message}
                                </Typography>
                              </FormControl>
                            )}
                          />
                        </Grid>

                        {/* State - Half Width */}
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="state"
                            control={control}
                            render={({ field }) => (
                              <>
                                {/* Show dropdown for India (country_id = 100), text field for others */}
                                {watch('country_id') === 100 ? (
                                  <FormControl
                                    fullWidth
                                    margin="normal"
                                    error={!!errors.state}
                                  >
                                    <InputLabel id="state-select-label">
                                      State <span style={{ color: 'red' }}>*</span>
                                    </InputLabel>
                                    <Select
                                      {...field}
                                      labelId="state-select-label"
                                      label={
                                        <span>
                                          State <span style={{ color: 'red' }}>*</span>
                                        </span>
                                      }
                                      sx={{ height: selectBoxHeight }}
                                      onChange={(e) => {
                                        const selectedStateId = e.target.value;
                                        const selectedState = states.find(state => state.id === selectedStateId);
                                        if (selectedState) {
                                          setValue('state', selectedState.name);
                                          setValue('state_id', selectedState.id);
                                        }
                                      }}
                                      value={states.find(state => state.name === field.value)?.id || ''}
                                    >
                                      {states.map((state) => (
                                        <MenuItem key={state.id} value={state.id}>
                                          {state.name}
                                        </MenuItem>
                                      ))}
                                    </Select>
                                    <Typography color="error" variant="caption">
                                      {errors.state?.message}
                                    </Typography>
                                  </FormControl>
                                ) : (
                                  <TextField
                                    {...field}
                                    label={
                                      <span>
                                        State <span style={{ color: 'red' }}>*</span>
                                      </span>
                                    }
                                    variant="outlined"
                                    fullWidth
                                    margin="normal"
                                    error={!!errors.state}
                                    helperText={errors.state?.message || ''}
                                    InputProps={{
                                      style: { fontSize: '0.875rem' },
                                    }}
                                    InputLabelProps={{
                                      shrink: true,
                                    }}
                                    onChange={(e) => {
                                      field.onChange(e.target.value.toUpperCase());
                                      setValue('state_id', null); // Set state_id to null for non-India countries
                                    }}
                                    sx={{
                                      '& .MuiInputBase-input': { 
                                        textTransform: 'uppercase' 
                                      },
                                    }}
                                  />
                                )}
                              </>
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* City & Postal Code Row */}
                      <Grid container spacing={2}>
                        {/* City - Half Width */}
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="city"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                                label={
                                  <span>
                                    City <span style={{ color: 'red' }}>*</span>
                                  </span>
                                }
                                variant="outlined"
                                fullWidth
                                margin="normal"
                                error={!!errors.city}
                                helperText={errors.city?.message || ''}
                                InputProps={{
                                  style: { fontSize: '0.875rem' },
                                }}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                                sx={{
                                  '& .MuiInputBase-input': { 
                                    textTransform: 'uppercase' 
                                  },
                                }}
                              />
                            )}
                          />
                        </Grid>

                        {/* Postal Code - Half Width */}
                        <Grid item xs={12} md={6}>
                          <Controller
                            name="postal_code"
                            control={control}
                            render={({ field }) => (
                              <TextField
                                {...field}
                                label={
                                  <span>
                                    Pin Code <span style={{ color: 'red' }}>*</span>
                                  </span>
                                }
                                variant="outlined"
                                type="text"
                                fullWidth
                                margin="normal"
                                error={!!errors.postal_code}
                                helperText={errors.postal_code?.message || ''}
                                InputProps={{
                                  style: { fontSize: '0.875rem' },
                                  inputProps: {
                                    maxLength: 10, // Optional: Add max length for postal codes
                                  },
                                }}
                                InputLabelProps={{
                                  shrink: true,
                                }}
                              />
                            )}
                          />
                        </Grid>
                      </Grid>

                      {/* Permanent Address Same as Residential Row */}
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Controller
                            name="permanentSameAsResidential"
                            control={control}
                            render={({ field }) => (
                              <FormControlLabel
                                control={
                                  <Checkbox 
                                    {...field} 
                                    checked={field.value} 
                                    color="primary" 
                                    onChange={(e) => {
                                      // First update the form value
                                      field.onChange(e);
                                      // Then run our additional handler for accordion toggling and field clearing
                                      handlePermanentCheckbox(e);
                                    }}
                                  />
                                }
                                label={
                                  <MDTypography variant="button" color="text" fontWeight="medium">
                                    Permanent address same as residential address
                                  </MDTypography>
                                }
                              />
                            )}
                          />
                        </Grid>
                      </Grid>
                      {/* Permanent Address Accordion Row */}
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Accordion
                            expanded={permanentAccordionOpen}
                            onChange={() => setPermanentAccordionOpen((prev) => !prev)}
                          >
                            <AccordionSummary
                              expandIcon={<ArrowDropDownIcon />}
                              aria-controls="panel1-content"
                              id="panel1-header"
                            >
                              <Typography
                                component="span"
                                fontSize="1rem"
                                color={'#00798E'}
                                fontWeight="bold"
                              >
                                Permanent Address
                              </Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                              <Typography>
                                <MDBox>
                                  {/* Address Line 1 */}
                                  <Controller
                                    name="permanent_address.address_line_1"
                                    control={control}
                                    render={({ field }) => (
                                      <TextField
                                        {...field}
                                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                                        label="Address Line 1"
                                        variant="outlined"
                                        fullWidth
                                        margin="normal"
                                        disabled={permanentSameAsResidential}
                                        error={!!errors.permanent_address?.address_line_1}
                                        helperText={
                                          errors.permanent_address?.address_line_1?.message
                                        }
                                        sx={{
                                          '& .MuiInputBase-input': { 
                                            textTransform: 'uppercase' 
                                          },
                                        }}
                                      />
                                    )}
                                  />
                                  {/* Address Line 2 (optional) */}
                                  <Controller
                                    name="permanent_address.address_line_2"
                                    control={control}
                                    render={({ field }) => (
                                      <TextField
                                        {...field}
                                        onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                                        label="Address Line 2"
                                        variant="outlined"
                                        fullWidth
                                        margin="normal"
                                        disabled={permanentSameAsResidential}
                                        error={!!errors.permanent_address?.address_line_2}
                                        helperText={
                                          errors.permanent_address?.address_line_2?.message
                                        }
                                        sx={{
                                          '& .MuiInputBase-input': { 
                                            textTransform: 'uppercase' 
                                          },
                                        }}
                                      />
                                    )}
                                  />

                                  <Grid container spacing={2}>
                                    {/* Permanent Country - Half Width */}
                                    <Grid item xs={12} md={6}>
                                      <Controller
                                        name="permanent_address.country_id"
                                        control={control}
                                        render={({ field }) => (
                                          <FormControl
                                            fullWidth
                                            margin="normal"
                                            error={!!errors.permanent_address?.country_id}
                                          >
                                            <InputLabel
                                              id="permanent-country-select-label"
                                              shrink
                                              sx={{
                                                backgroundColor: 'white',
                                                padding: '0 4px',
                                                marginLeft: '-4px',
                                              }}
                                            >
                                              Country
                                            </InputLabel>
                                            <Select
                                              {...field}
                                              labelId="permanent-country-select-label"
                                              label="Country"
                                              disabled={permanentSameAsResidential}
                                              sx={{
                                                height: selectBoxHeight,
                                                fontSize: '0.875rem',
                                              }}
                                            >
                                              {countries.map((country) => (
                                                <MenuItem
                                                  key={country.id}
                                                  value={country.id}
                                                  sx={{ fontSize: '0.875rem' }}
                                                >
                                                  {country.country_name}
                                                </MenuItem>
                                              ))}
                                            </Select>
                                            <Typography color="error" variant="caption">
                                              {errors.permanent_address?.country_id?.message}
                                            </Typography>
                                          </FormControl>
                                        )}
                                      />
                                    </Grid>

                                    {/* Permanent State - Half Width */}
                                    <Grid item xs={12} md={6}>
                                      <Controller
                                        name="permanent_address.state"
                                        control={control}
                                        render={({ field }) => (
                                          <>
                                            {/* Show dropdown for India (country_id = 100), text field for others */}
                                            {watch('permanent_address.country_id') === 100 ? (
                                              <FormControl
                                                fullWidth
                                                margin="normal"
                                                disabled={permanentSameAsResidential}
                                                error={!!errors.permanent_address?.state}
                                              >
                                                <InputLabel id="permanent-state-select-label">
                                                  State <span style={{ color: 'red' }}>*</span>
                                                </InputLabel>
                                                <Select
                                                  {...field}
                                                  labelId="permanent-state-select-label"
                                                  label={
                                                    <span>
                                                      State <span style={{ color: 'red' }}>*</span>
                                                    </span>
                                                  }
                                                  sx={{ height: selectBoxHeight }}
                                                  onChange={(e) => {
                                                    const selectedStateId = e.target.value;
                                                    const selectedState = states.find(state => state.id === selectedStateId);
                                                    if (selectedState) {
                                                      setValue('permanent_address.state', selectedState.name);
                                                      setValue('permanent_address.state_id', selectedState.id);
                                                    }
                                                  }}
                                                  value={states.find(state => state.name === field.value)?.id || ''}
                                                >
                                                  {states.map((state) => (
                                                    <MenuItem key={state.id} value={state.id}>
                                                      {state.name}
                                                    </MenuItem>
                                                  ))}
                                                </Select>
                                                <Typography color="error" variant="caption">
                                                  {errors.permanent_address?.state?.message}
                                                </Typography>
                                              </FormControl>
                                            ) : (
                                              <TextField
                                                {...field}
                                                label={
                                                  <span>
                                                    State <span style={{ color: 'red' }}>*</span>
                                                  </span>
                                                }
                                                variant="outlined"
                                                fullWidth
                                                margin="normal"
                                                disabled={permanentSameAsResidential}
                                                error={!!errors.permanent_address?.state}
                                                helperText={
                                                  errors.permanent_address?.state?.message || ''
                                                }
                                                InputProps={{
                                                  style: { fontSize: '0.875rem' },
                                                }}
                                                InputLabelProps={{
                                                  shrink: true,
                                                }}
                                                onChange={(e) => {
                                                  field.onChange(e.target.value.toUpperCase());
                                                  setValue('permanent_address.state_id', null); // Set state_id to null for non-India countries
                                                }}
                                                sx={{
                                                  '& .MuiInputBase-input': { 
                                                    textTransform: 'uppercase' 
                                                  },
                                                }}
                                              />
                                            )}
                                          </>
                                        )}
                                      />
                                    </Grid>
                                  </Grid>

                                  <Grid container spacing={2}>
                                    {/* Permanent City - Half Width */}
                                    <Grid item xs={12} md={6}>
                                      <Controller
                                        name="permanent_address.city"
                                        control={control}
                                        render={({ field }) => (
                                          <TextField
                                            {...field}
                                            onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                                            label={
                                              <span>
                                                City <span style={{ color: 'red' }}>*</span>
                                              </span>
                                            }
                                            variant="outlined"
                                            fullWidth
                                            margin="normal"
                                            disabled={permanentSameAsResidential}
                                            error={!!errors.permanent_address?.city}
                                            helperText={
                                              errors.permanent_address?.city?.message || ''
                                            }
                                            InputProps={{
                                              style: { fontSize: '0.875rem' },
                                            }}
                                            InputLabelProps={{
                                              shrink: true,
                                            }}
                                            sx={{
                                              '& .MuiInputBase-input': { 
                                                textTransform: 'uppercase' 
                                              },
                                            }}
                                          />
                                        )}
                                      />
                                    </Grid>

                                    {/* Permanent Postal Code - Half Width */}
                                    <Grid item xs={12} md={6}>
                                      <Controller
                                        name="permanent_address.postal_code"
                                        control={control}
                                        render={({ field }) => (
                                          <TextField
                                            {...field}
                                            label="Pin Code"
                                            variant="outlined"
                                            type="text"
                                            fullWidth
                                            margin="normal"
                                            disabled={permanentSameAsResidential}
                                            error={!!errors.permanent_address?.postal_code}
                                            helperText={
                                              errors.permanent_address?.postal_code?.message || ' '
                                            }
                                            InputProps={{
                                              style: { fontSize: '0.875rem' },
                                              inputProps: {
                                                maxLength: 10,
                                              },
                                            }}
                                            InputLabelProps={{
                                              shrink: true,
                                            }}
                                          />
                                        )}
                                      />
                                    </Grid>
                                  </Grid>
                                </MDBox>
                              </Typography>
                            </AccordionDetails>
                          </Accordion>
                        </Grid>
                      </Grid>

                      {/* Doctor Dropdown Row */}
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Controller
                            name="doctor_id"
                            control={control}
                            render={({ field }) => (
                              <FormControl fullWidth margin="normal" error={!!errors.doctor_id}>
                                <InputLabel id="doctor-select-label">
                                  Consult With <span style={{ color: 'red' }}>*</span>
                                </InputLabel>
                                <Select
                                  {...field}
                                  labelId="doctor-select-label"
                                  label={
                                    <span>
                                      Consult With <span style={{ color: 'red' }}>*</span>
                                    </span>
                                  }
                                  sx={{ height: selectBoxHeight }}
                                >
                                  {doctors.map((doctor) => (
                                    <MenuItem key={doctor.user_id} value={doctor.user_id}>
                                      {doctor.name}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Typography color="error" variant="caption">
                                  {errors.doctor_id?.message}
                                </Typography>
                              </FormControl>
                            )}
                          />
                        </Grid>
                      </Grid>
                      {/* Referred By Dropdown Row */}
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Controller
                            name="referred_by_id"
                            control={control}
                            render={({ field }) => (
                              <FormControl
                                fullWidth
                                margin="normal"
                                error={!!errors.referred_by_id}
                              >
                                <InputLabel id="referred-by-select-label">
                                  Referred By <span style={{ color: 'red' }}>*</span>
                                </InputLabel>
                                <Select
                                  {...field}
                                  labelId="referred-by-select-label"
                                  label={
                                    <span>
                                      Referred By <span style={{ color: 'red' }}>*</span>
                                    </span>
                                  }
                                  sx={{ height: selectBoxHeight }}
                                >
                                  {referredBy.map((option) => (
                                    <MenuItem key={option.id} value={option.id}>
                                      {option.option_value}
                                    </MenuItem>
                                  ))}
                                </Select>
                                <Typography color="error" variant="caption">
                                  {errors.referred_by_id?.message}
                                </Typography>
                              </FormControl>
                            )}
                          />
                        </Grid>
                      </Grid>
                      {/* Referred By Name Row */}
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          {watch('referred_by_id') === 3 ? (
                            <Controller
                              name="referred_by_name"
                              control={control}
                              render={({ field }) => (
                                <FormControl
                                  fullWidth
                                  margin="normal"
                                  error={!!errors.referred_by_name}
                                >
                                  <InputLabel id="referred-by-doctor-label">
                                    Referred By Doctor <span style={{ color: 'red' }}>*</span>
                                  </InputLabel>
                                  <Select
                                    {...field}
                                    labelId="referred-by-doctor-label"
                                    label={
                                      <span>
                                        Referred By Doctor <span style={{ color: 'red' }}>*</span>
                                      </span>
                                    }
                                    sx={{ height: selectBoxHeight }}
                                    onChange={(e) => {
                                      const selectedValue = e.target.value;
                                      const selectedOption = referredByDoctor.find(option => option.option_value === selectedValue);
                                      
                                      // Always call field.onChange to update the form field
                                      field.onChange(e);
                                      
                                      if (selectedValue === 'Other') {
                                        // Handle "Other" option
                                        setValue('referred_by_doctor_name', '');
                                        setValue('referred_by_doctor_id', null);
                                      } else if (selectedOption && selectedOption.id !== 'other') {
                                        // Handle regular doctor options
                                        setValue('referred_by_doctor_name', selectedValue);
                                        setValue('referred_by_doctor_id', selectedOption.id);
                                      }
                                    }}
                                  >
                                    {referredByDoctor.map((option) => (
                                      <MenuItem key={option.id} value={option.option_value}>
                                        {option.option_value}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                  <Typography color="error" variant="caption">
                                    {errors.referred_by_name?.message}
                                  </Typography>
                                </FormControl>
                              )}
                            />
                          ) : (
                            <Controller
                              name="referred_by_name"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label={
                                    <span>
                                      Referred By Name <span style={{ color: 'red' }}>*</span>
                                    </span>
                                  }
                                  variant="outlined"
                                  fullWidth
                                  margin="normal"
                                  error={!!errors.referred_by_name}
                                  helperText={errors.referred_by_name?.message}
                                />
                              )}
                            />
                          )}
                        </Grid>
                        {/* Custom Doctor Name Field - Shows when "Other" is selected */}
                        {showCustomDoctorInput && watch('referred_by_id') === 3 && (
                          <Grid item xs={12}>
                            <Controller
                              name="custom_doctor_name"
                              control={control}
                              render={({ field }) => (
                                <TextField
                                  {...field}
                                  label={
                                    <span>
                                      Enter Doctor Name <span style={{ color: 'red' }}>*</span>
                                    </span>
                                  }
                                  placeholder="Please enter the doctor's name"
                                  variant="outlined"
                                  fullWidth
                                  margin="normal"
                                  error={!!errors.custom_doctor_name}
                                  helperText={errors.custom_doctor_name?.message}
                                  onChange={(e) => {
                                    field.onChange(e.target.value.toUpperCase());
                                    setValue('referred_by_doctor_name', e.target.value.toUpperCase());
                                  }}
                                  sx={{
                                    '& .MuiInputBase-input': { 
                                      textTransform: 'uppercase' 
                                    },
                                  }}
                                />
                              )}
                            />
                          </Grid>
                        )}
                      </Grid>
                    </Grid>
                    {/* Spouse Details (conditional) */}
                    <Grid
                      item
                      xs={12}
                      md={6}
                      display={marital_status === 'married' || marital_status === 'widowed' ? 'block' : 'none'}
                    >
                      {(marital_status === 'married' || marital_status === 'widowed') && (
                        <>
                          <MDBox>
                            <MDTypography
                              variant="h5"
                              fontWeight="bold"
                              gutterBottom
                              sx={{
                                textAlign: 'center',
                                padding: '12px',
                                borderBottom: '1px solid #00798E',
                                display: 'flex',
                                justifyContent: 'center',
                              }}
                            >
                              Spouse Details
                            </MDTypography>
                          </MDBox>
                          {/* Spouse Name Row */}
                          <Grid container spacing={2}>
                            {/* Spouse First Name - Half Width */}
                            <Grid item xs={12} md={6}>
                              <Controller
                                name="spouse_first_name"
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                                    label={
                                      <span>
                                        Spouse First Name <span style={{ color: 'red' }}>*</span>
                                      </span>
                                    }
                                    variant="outlined"
                                    fullWidth
                                    margin="normal"
                                    error={!!errors.spouse_first_name}
                                    helperText={errors.spouse_first_name?.message || ''}
                                    // InputProps={{
                                    //   style: { fontSize: "0.875rem" },
                                    // }}
                                    // InputLabelProps={{
                                    //   shrink: true,
                                    // }}
                                    sx={{
                                      '& .MuiInputBase-input': { 
                                        textTransform: 'uppercase' 
                                      },
                                    }}
                                  />
                                )}
                              />
                            </Grid>

                            {/* Spouse Last Name - Half Width */}
                            <Grid item xs={12} md={6}>
                              <Controller
                                name="spouse_last_name"
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    onChange={(e) => field.onChange(e.target.value.toUpperCase())}
                                    label={
                                      <span>
                                        Spouse Last Name <span style={{ color: 'red' }}>*</span>
                                      </span>
                                    }
                                    variant="outlined"
                                    fullWidth
                                    margin="normal"
                                    error={!!errors.spouse_last_name}
                                    helperText={errors.spouse_last_name?.message || ''}
                                    // InputProps={{
                                    //   style: { fontSize: "0.875rem" },
                                    // }}
                                    // InputLabelProps={{
                                    //   shrink: true,
                                    // }}
                                    sx={{
                                      '& .MuiInputBase-input': { 
                                        textTransform: 'uppercase' 
                                      },
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>

                          {/* Spouse Gender Row */}
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Controller
                                name="spouse_gender"
                                control={control}
                                render={({ field }) => (
                                  <FormControl
                                    fullWidth
                                    margin="normal"
                                    error={!!errors.spouse_gender}
                                  >
                                    <InputLabel id="spouse-gender-label">Spouse Gender</InputLabel>
                                    <Select
                                      {...field}
                                      labelId="spouse-gender-label"
                                      label="Spouse Gender"
                                      sx={{
                                        height: selectBoxHeight,
                                      }}
                                    >
                                      <MenuItem value="F">Female</MenuItem>
                                      <MenuItem value="M">Male</MenuItem>
                                    </Select>
                                    <Typography color="error" variant="caption">
                                      {errors.spouse_gender?.message}
                                    </Typography>
                                  </FormControl>
                                )}
                              />
                            </Grid>
                          </Grid>

                          {/* Spouse Date of Birth Row */}
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Controller
                                name="spouse_date_of_birth"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => (
                                  <FormControl
                                    fullWidth
                                    margin="normal"
                                    error={!!errors.spouse_date_of_birth}
                                  >
                                    <MDDatePicker
                                      value={field.value || ''}
                                      onChange={(apiDate) => {
                                        field.onChange(apiDate);
                                      }}
                                      label={
                                        <span>
                                          Spouse Date of Birth{' '}
                                          <span style={{ color: 'red' }}>*</span>
                                        </span>
                                      }
                                      textFieldProps={{ sx: { width: '100%' } }}
                                      disableFuture={true}
                                    />
                                    <Typography color="error" variant="caption">
                                      {errors.spouse_date_of_birth?.message}
                                    </Typography>
                                  </FormControl>
                                )}
                              />
                            </Grid>
                          </Grid>

                          {/* Spouse Mobile Number Row */}
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Controller
                                name="spouse_mobile_number"
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    label={
                                      <span>
                                        Spouse Primary Mobile Number{' '}
                                        <span style={{ color: 'red' }}>*</span>
                                      </span>
                                    }
                                    placeholder={nationality === "Indian" ? 
                                      "Spouse Mobile Number (10 digit)" : 
                                      "Spouse Mobile Number (Max 15 digit)"}
                                    variant="outlined"
                                    type="text"
                                    fullWidth
                                    margin="normal"
                                    error={!!errors.spouse_mobile_number}
                                    helperText={
                                      errors.spouse_mobile_number?.message || 
                                      (nationality_value === 'Indian' ? '10 digits required' : '8 to 15 digits required')
                                    }
                                    InputProps={{
                                      inputProps: {
                                        maxLength: nationality_value === 'Indian' ? 10 : 15,
                                        pattern: '[0-9]*',
                                        inputMode: 'numeric',
                                      },
                                    }}
                                    onChange={(e) => {
                                      const value = e.target.value.replace(/[^0-9]/g, '');
                                      field.onChange(value);
                                    }}
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>

                          {/* Spouse Occupation Row */}
                          <Grid container spacing={2}>
                            <Grid item xs={12}>
                              <Controller
                                name="spouse_occupation"
                                control={control}
                                render={({ field }) => (
                                  <TextField
                                    {...field}
                                    label={
                                      <span>
                                        Spouse Occupation <span style={{ color: 'red' }}>*</span>
                                      </span>
                                    }
                                    variant="outlined"
                                    fullWidth
                                    margin="normal"
                                    error={!!errors.spouse_occupation}
                                    helperText={errors.spouse_occupation?.message}
                                  />
                                )}
                              />
                            </Grid>
                          </Grid>
                        </>
                      )}
                    </Grid>
                    <MDBox display="flex" justifyContent="center" mt={4} width="100%">
                      <button
                        type="submit"
                        style={{
                          padding: '10px 24px',
                          background: '#00798E',
                          color: '#fff',
                          border: 'none',
                          borderRadius: 4,
                          cursor: 'pointer',
                          fontSize: '1rem',
                          fontWeight: '500',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                          transition: 'background 0.3s ease',
                        }}
                        onMouseOver={(e) => (e.currentTarget.style.background = '#006379')}
                        onMouseOut={(e) => (e.currentTarget.style.background = '#00798E')}
                      >
                        <SaveIcon fontSize="small" />
                        Save
                      </button>
                    </MDBox>
                  </Grid>
                </form>
              </Card>
            </Grid>
          </Grid>
        )}
      </MDBox>

      {/*<MDBox pt={3} pb={6}>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" mt={2}>
          <button
            type="button"
            style={{
              padding: '10px 24px',
              background: '#e0e0e0',
              color: '#333',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
            }}
            onClick={() => setStep((prev) => Math.max(1, prev - 1))}
            disabled={step === 1}
          >
            Previous
          </button>
          <button
            type="button"
            style={{
              padding: '10px 24px',
              background: '#00798E',
              color: '#fff',
              border: 'none',
              borderRadius: 4,
              cursor: 'pointer',
              fontSize: '1rem',
              fontWeight: '500',
            }}
            onClick={() => setStep((prev) => Math.min(3, prev + 1))}
            disabled={step === 3}
          >
            Next
          </button>
        </MDBox>
      </MDBox> */ }

      <Footer />
    </DashboardLayout>
  );
}

export default PatientRegistration;
